import { useCallback, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Checkbox } from '@factorialco/f0-react'
import { RichTextEditor, RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import { Calendar } from '@factorialco/f0-react/icons/app'
import moment from 'moment'

import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import SchedulePostModal from 'modules/posts/components/SchedulePost/SchedulePostModal'
import { useCreatePost } from 'modules/posts/hooks/useCreatePost'

interface CommunityQuickPostEditorProps {
  groupId: number
}

function CommunityQuickPostEditor({ groupId }: CommunityQuickPostEditorProps) {
  const { editorConfig, loading: loadingEditorConfig } = useRichTextEditor()
  const [editorContent, setEditorContent] = useState('')
  const editorRef = useRef<RichTextEditorHandle>(null)

  const { createPost, loadingCreatePost } = useCreatePost()

  const [turnOffInteractions, setTurnOffInteractions] = useState(false)
  const [doNotSendNotification, setDoNotSendNotification] = useState(false)
  const [showScheduleModal, setShowScheduleModal] = useState(false)

  const savePost = useCallback(
    async (scheduledDate?: string) => {
      const result = await createPost({
        description: editorContent,
        mentionedAccessIds: [],
        postGroupId: groupId,
        publishedAt: scheduledDate || moment().toISOString(),
        title: getTitleFromContent(editorContent),
        type: 'announcement',
        allowCommentsAndReactions: !turnOffInteractions,
        sendNotifications: !doNotSendNotification,
      })
      const handledResult = handleResult(result, () => {})

      if (handledResult.success) {
        clearEditor()
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('posts.communities.quick_post.error_message'),
        })
      }
    },
    [editorContent, createPost, groupId, turnOffInteractions, doNotSendNotification]
  )

  const handleSchedule = useCallback(
    (scheduledDate: string) => {
      savePost(scheduledDate)
      setShowScheduleModal(false)
    },
    [savePost]
  )

  function getTitleFromContent(content: string): string {
    if (!content.trim()) return i18n.t('posts.communities.quick_post.default_title')

    const plainText = content.replace(/<[^>]*>/g, '').trim()
    const maxLength = 60
    if (plainText.length <= maxLength) return plainText

    const truncated = plainText.substring(0, maxLength)
    const lastSpaceIndex = truncated.lastIndexOf(' ')
    const contentFirstWords =
      lastSpaceIndex > 0 ? truncated.substring(0, lastSpaceIndex) : truncated
    return `${contentFirstWords}...`
  }

  function clearEditor() {
    setEditorContent('')
    editorRef.current?.setContent('')
  }

  return loadingEditorConfig ? (
    <RichTextEditor.Skeleton rows={1} />
  ) : (
    <Box gap="s16">
      <RichTextEditor
        ref={editorRef}
        {...editorConfig}
        title={getTitleFromContent(editorContent)}
        placeholder={i18n.t('posts.communities.quick_post.placeholder')}
        maxCharacters={10000}
        initialEditorState={{ content: editorContent }}
        onChange={(result) => setEditorContent(result?.value ?? '')}
        primaryAction={{
          action: {
            label: loadingCreatePost
              ? i18n.t('common.saving')
              : i18n.t('posts.communities.quick_post.button'),
            onClick: () => savePost(),
            variant: 'default',
            disabled: loadingCreatePost || !editorContent.trim(),
          },
          subActions: [
            {
              label: i18n.t('posts.communities.schedule_post.title'),
              onClick: () => setShowScheduleModal(true),
              icon: Calendar,
            },
          ],
        }}
      />
      <F0Checkbox
        title={i18n.t('posts.communities.quick_post.turn_off_interactions')}
        checked={turnOffInteractions}
        onCheckedChange={setTurnOffInteractions}
      />
      <F0Checkbox
        title={i18n.t('posts.communities.quick_post.do_not_send_notification')}
        checked={doNotSendNotification}
        onCheckedChange={setDoNotSendNotification}
      />
      <SchedulePostModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onConfirm={handleSchedule}
      />
    </Box>
  )
}

export default CommunityQuickPostEditor
